import { useMemo, useState } from "react";
import { CreateRng, SampleNormal, Histogram } from "../../../../lib/sampleStats";
import {
  Axes, ChartSvg, Legend, LinearScale, M, NiceTicks, PointerToViewBoxX, SERIES, SURFACE_STROKE, Tooltip, TopRoundedBar, W, H, type TooltipState,
} from "../playground/ChartKit";

const LABEL = "fill-[#0A0A0F] dark:fill-white text-[11px] font-medium";
const MUTED = "fill-[#5C5C66] dark:fill-[#9A9A9A] text-[10px]";
const PLOT_W = W - M.left - M.right;
const PLOT_BOTTOM = H - M.bottom;

export function BarPreview() {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const data = useMemo(() => {
    const rng = CreateRng(11);
    return ["North", "South", "East", "West", "Central"].map((name) => ({ name, value: Math.round(25 + rng() * 60) }));
  }, []);
  const y = LinearScale([0, 100], [PLOT_BOTTOM, M.top]);
  const band = PLOT_W / data.length, barW = band * 0.56;
  return (
    <div className="relative">
      <ChartSvg label="Bar chart of values by region" onLeave={() => setTip(null)}>
        <Axes xTicks={[]} yTicks={NiceTicks(0, 100)} x={(v) => v} y={y} xLabel="" yLabel="Value" />
        {data.map((d, i) => {
          const x = M.left + band * i + (band - barW) / 2;
          return (
            <g key={d.name} onMouseEnter={() => setTip({ x: x + barW / 2, y: y(d.value), lines: [d.name, `Value ${d.value}`] })}>
              <rect x={M.left + band * i} y={M.top} width={band} height={PLOT_BOTTOM - M.top} fill="transparent" />
              <path d={TopRoundedBar(x, y(d.value), barW, PLOT_BOTTOM - y(d.value))} className={SERIES[0].fill} />
              <text x={x + barW / 2} y={PLOT_BOTTOM + 16} textAnchor="middle" className={MUTED}>{d.name}</text>
            </g>
          );
        })}
      </ChartSvg>
      <Tooltip tip={tip} />
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function RandomWalk(seed: number, start: number, drift: number): number[] {
  const rng = CreateRng(seed);
  let v = start;
  return MONTHS.map(() => (v = Math.max(5, v + drift + SampleNormal(rng, 0, 4))));
}

export function LinePreview() {
  const [index, setIndex] = useState<number | null>(null);
  const series = useMemo(() => [RandomWalk(3, 40, 1.8), RandomWalk(9, 55, -0.6)], []);
  const x = LinearScale([0, MONTHS.length - 1], [M.left, W - M.right]);
  const y = LinearScale([0, 100], [PLOT_BOTTOM, M.top]);
  const path = (vals: number[]) => vals.map((v, i) => `${i ? "L" : "M"}${x(i)} ${y(v)}`).join(" ");
  const tip = index === null ? null : { x: x(index), y: M.top + 30, lines: [MONTHS[index], ...series.map((s, i) => `${SERIES[i].name}: ${s[index].toFixed(1)}`)] };
  return (
    <div className="relative">
      <div className="mb-2"><Legend items={SERIES.slice(0, 2).map((s) => ({ label: s.name, swatch: s.swatch }))} /></div>
      <ChartSvg label="Line chart of two series over twelve months" onLeave={() => setIndex(null)}>
        <Axes xTicks={[]} yTicks={NiceTicks(0, 100)} x={x} y={y} xLabel="" yLabel="Value" />
        {MONTHS.map((m, i) => i % 2 === 0 && <text key={m} x={x(i)} y={PLOT_BOTTOM + 16} textAnchor="middle" className={MUTED}>{m}</text>)}
        {series.map((s, i) => <path key={i} d={path(s)} fill="none" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" className={SERIES[i].stroke} />)}
        {index !== null && (
          <g pointerEvents="none">
            <line x1={x(index)} x2={x(index)} y1={M.top} y2={PLOT_BOTTOM} className="stroke-[#0A0A0F]/40 dark:stroke-white/40" />
            {series.map((s, i) => <circle key={i} cx={x(index)} cy={y(s[index])} r={4} strokeWidth={2} className={`${SERIES[i].fill} ${SURFACE_STROKE}`} />)}
          </g>
        )}
        <rect x={M.left} y={M.top} width={PLOT_W} height={PLOT_BOTTOM - M.top} fill="transparent"
          onMouseMove={(e) => setIndex(Math.round(LinearScale([M.left, W - M.right], [0, MONTHS.length - 1])(PointerToViewBoxX(e))))} />
      </ChartSvg>
      <Tooltip tip={tip ? { ...tip, x: Math.max(M.left, Math.min(W - M.right, tip.x)) } : null} />
    </div>
  );
}

const SHARES = [{ name: "Group A", value: 38 }, { name: "Group B", value: 27 }, { name: "Group C", value: 21 }, { name: "Group D", value: 14 }];

function ArcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const p = (a: number) => `${cx + r * Math.sin(a)} ${cy - r * Math.cos(a)}`;
  return `M${cx} ${cy} L${p(a0)} A${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${p(a1)} Z`;
}

export function PiePreview() {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const cx = W / 2, cy = H / 2, r = 105;
  let angle = 0;
  const slices = SHARES.map((s) => {
    const a0 = angle, a1 = (angle += (s.value / 100) * 2 * Math.PI);
    return { ...s, a0, a1, mid: (a0 + a1) / 2 };
  });
  return (
    <div className="relative">
      <div className="mb-2"><Legend items={SHARES.map((s, i) => ({ label: s.name, swatch: SERIES[i].swatch }))} /></div>
      <ChartSvg label="Pie chart of four group shares" onLeave={() => setTip(null)}>
        {slices.map((s, i) => (
          <g key={s.name} onMouseEnter={() => setTip({ x: cx + r * 0.6 * Math.sin(s.mid), y: cy - r * 0.6 * Math.cos(s.mid), lines: [s.name, `${s.value}% of total`] })}>
            <path d={ArcPath(cx, cy, r, s.a0, s.a1)} strokeWidth={2} className={`${SERIES[i].fill} ${SURFACE_STROKE}`} />
            <text x={cx + (r + 18) * Math.sin(s.mid)} y={cy - (r + 18) * Math.cos(s.mid)} dy="0.32em" textAnchor={Math.sin(s.mid) >= 0 ? "start" : "end"} className={LABEL}>{s.value}%</text>
          </g>
        ))}
      </ChartSvg>
      <Tooltip tip={tip} />
    </div>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const SLOTS = ["8", "9", "10", "11", "12", "13", "14", "15"];

export function HeatmapPreview() {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const grid = useMemo(() => {
    const rng = CreateRng(21);
    return DAYS.map((_, r) => SLOTS.map((_, c) => Math.round(Math.max(0, Math.min(100, 30 + 40 * Math.sin((c + r) / 3) + rng() * 30)))));
  }, []);
  const cellW = PLOT_W / SLOTS.length, cellH = (PLOT_BOTTOM - M.top) / DAYS.length;
  return (
    <div className="relative">
      <div className="mb-2 flex items-center gap-2 text-xs text-[#5C5C66] dark:text-[#9A9A9A]">
        Low <span className="h-2.5 w-24 rounded-sm bg-gradient-to-r from-[#1E4FD8]/10 to-[#1E4FD8] dark:from-[#4A90D9]/10 dark:to-[#4A90D9]" /> High
      </div>
      <ChartSvg label="Heatmap of values by day and hour" onLeave={() => setTip(null)}>
        {DAYS.map((d, r) => <text key={d} x={M.left - 8} y={M.top + cellH * (r + 0.5)} dy="0.32em" textAnchor="end" className={MUTED}>{d}</text>)}
        {SLOTS.map((s, c) => <text key={s} x={M.left + cellW * (c + 0.5)} y={PLOT_BOTTOM + 16} textAnchor="middle" className={MUTED}>{s}:00</text>)}
        {grid.map((row, r) => row.map((v, c) => (
          <rect
            key={`${r}-${c}`} x={M.left + cellW * c + 1} y={M.top + cellH * r + 1} width={cellW - 2} height={cellH - 2} rx={3}
            className={SERIES[0].fill} style={{ fillOpacity: 0.1 + (v / 100) * 0.9 }}
            onMouseEnter={() => setTip({ x: M.left + cellW * (c + 0.5), y: M.top + cellH * (r + 0.5), lines: [`${DAYS[r]} · ${SLOTS[c]}:00`, `Value ${v}`] })}
          />
        )))}
      </ChartSvg>
      <Tooltip tip={tip} />
    </div>
  );
}

export function HistogramPreview() {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const bins = useMemo(() => {
    const rng = CreateRng(5);
    return Histogram(Array.from({ length: 400 }, () => SampleNormal(rng, 50, 10)), 20);
  }, []);
  const maxCount = Math.max(...bins.map((b) => b.count));
  const x = LinearScale([bins[0].start, bins[bins.length - 1].end], [M.left, W - M.right]);
  const y = LinearScale([0, Math.ceil(maxCount / 10) * 10], [PLOT_BOTTOM, M.top]);
  return (
    <div className="relative">
      <ChartSvg label="Histogram of 400 sample values" onLeave={() => setTip(null)}>
        <Axes xTicks={NiceTicks(bins[0].start, bins[bins.length - 1].end)} yTicks={NiceTicks(0, Math.ceil(maxCount / 10) * 10)} x={x} y={y} xLabel="Value" yLabel="Count" />
        {bins.map((b) => (
          <path
            key={b.start} d={TopRoundedBar(x(b.start) + 1, y(b.count), x(b.end) - x(b.start) - 2, PLOT_BOTTOM - y(b.count), 3)} className={SERIES[0].fill}
            onMouseEnter={() => setTip({ x: x((b.start + b.end) / 2), y: y(b.count), lines: [`${b.start.toFixed(1)} – ${b.end.toFixed(1)}`, `${b.count} values`] })}
          />
        ))}
      </ChartSvg>
      <Tooltip tip={tip} />
    </div>
  );
}
